import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Compass, ChevronRight, Check, Target } from '../../components/icons';
import { useAuth } from '../../contexts/AuthContext';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { fetchMyPathwaysAsCoach } from '../../lib/pathways';
import type { AssignedPathway } from '../../types/pathway';

export function MyAssignedPathways() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [pathways, setPathways] = useState<AssignedPathway[]>([]);

  useEffect(() => {
    if (!user) { navigate('/login'); return; }
    loadData();
  }, [user, navigate]);

  const loadData = async () => {
    if (!user?.id) return;
    try {
      const data = await fetchMyPathwaysAsCoach(user.id);
      setPathways(data);
    } catch (err) {
      console.error('Error loading assigned pathways:', err);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) return <LoadingSpinner message="Loading pathways..." />;

  return (
    <div className="min-h-screen freud-bg">
      <header className="bg-white/80 backdrop-blur-sm border-b border-cream-300">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div>
            <h1 className="freud-heading-lg text-chocolate-900">My Assigned Pathways</h1>
            <p className="text-sm text-chocolate-500">Learning journeys your partner has assigned to you</p>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {pathways.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="freud-card-elevated p-12 text-center"
          >
            <div className="w-16 h-16 bg-sage-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <Compass className="w-8 h-8 text-sage-600" />
            </div>
            <h2 className="freud-heading-xl text-chocolate-900 mb-3">No Pathways Assigned Yet</h2>
            <p className="freud-text-base text-chocolate-500 mb-8 max-w-md mx-auto">
              Your partner can assign transformation pathways for you to work through. Check back here once they do.
            </p>
          </motion.div>
        ) : (
          <div className="grid gap-4">
            {pathways.map((item, index) => {
              const progress = item.totalStages > 0
                ? Math.round((item.completedStages / item.totalStages) * 100)
                : 0;
              const isComplete = item.completedStages === item.totalStages && item.totalStages > 0;
              const notStarted = item.completedStages === 0;

              return (
                <motion.div
                  key={item.assignmentId}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <Link
                    to={`/coach/my-pathways/${item.assignmentId}`}
                    className="block freud-card-elevated p-6 hover:shadow-lg transition-all duration-300 group"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-start gap-4 flex-1 min-w-0">
                        <div className={`w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0 ${
                          isComplete
                            ? 'bg-sage-100'
                            : notStarted
                            ? 'bg-cream-200'
                            : 'bg-sage-50'
                        }`}>
                          {isComplete ? (
                            <Target className="w-6 h-6 text-sage-600" />
                          ) : (
                            <Compass className={`w-6 h-6 ${notStarted ? 'text-chocolate-400' : 'text-sage-500'}`} />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="freud-heading-base text-chocolate-900 truncate group-hover:text-sage-600 transition-colors">
                            {item.pathway.name}
                          </h3>
                          {item.pathway.description && (
                            <p className="text-sm text-chocolate-500 mt-1 line-clamp-2">{item.pathway.description}</p>
                          )}
                          <div className="flex items-center gap-3 mt-3">
                            <span className={`text-xs px-2.5 py-0.5 rounded-full font-medium ${
                              isComplete
                                ? 'bg-success-50 text-green-700'
                                : notStarted
                                ? 'bg-cream-200 text-chocolate-500'
                                : 'bg-sage-50 text-sage-600'
                            }`}>
                              {item.completedStages} / {item.totalStages} stages
                            </span>
                            {item.currentStageTitle && !isComplete && (
                              <span className="text-xs text-chocolate-400 flex items-center gap-1">
                                <ChevronRight className="w-3 h-3" />
                                Next: {item.currentStageTitle}
                              </span>
                            )}
                            {isComplete && (
                              <span className="text-xs text-green-600 flex items-center gap-1">
                                <Check className="w-3 h-3" />
                                Complete
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 flex-shrink-0">
                        <div className="text-right">
                          <span className="text-sm font-medium text-chocolate-600">{progress}%</span>
                        </div>
                        <ChevronRight className="w-5 h-5 text-chocolate-400 group-hover:text-sage-600 transition-colors" />
                      </div>
                    </div>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
